
import moment from 'moment'
import Api from '@/Plugins/api'
import Order from '@/Models/Order'
import Invoice from '@/Models/Invoice'
import Refund from '@/Models/Refund'
import PaymentMethod from '@/Models/Static/PaymentMethod'

const state = () => ({
  /**
   * @type Boolean
   */
  refunding: false,

  /**
   * @type Invoice
   */
  refunded: null,

  /**
   * @type Order
   */
  refund: null,
})

const getters = {
  cardPayments: state => state.refund?.orderInvoices[0].orderPayments
    .filter(payment => PaymentMethod.isCard(payment.paymentMethod))
}

const mutations = {
  refunding (state, v = true) {
    state.refunding = Boolean(v)
    if (! v) {
      state.refunded = null
      state.refund = null
    }
  },
  refunded (state, v) {
    if (! v instanceof Invoice) {
      throw new TypeError()
    }
    state.refunded = v
  },
  refund (state, v) {
    if (! v instanceof Order) {
      throw new TypeError()
    }
    state.refund = v
  },
  markAsRefunded (state) {
    if (state.refunded) {
      state.refunded.refunded = true
    }
  },
  updateVoidReason (state, { index, voidReason }) {
    state.refund.orderInvoices[0].orderPayments
      .filter(payment => PaymentMethod.isCard(payment.paymentMethod))
      [index]
      .voidReason = voidReason
  },
}

const actions = {
  // inpired by InvoiceRefunder @ Xef's APP
  async createRefund ({ commit, dispatch, rootState, rootGetters }, { order, invoice, voidReasons = [] }) {
    dispatch('log', { action: 'Refunding...', model: 'Invoice', model_id: invoice.id }, { root: true })
    const opened = moment().utc().format('YYYY-MM-DD HH:mm:ss')
    const lockedBy = rootState.UUID
    const tenantUser = {
      id: rootState.user.id,
      name: rootGetters['user/fullName']
    }
    const orderResponse = await Api(['getNewId', 'Order'], {
      opened,
      lockedBy,
      table_id: 0,
      tableName: `POS - ${tenantUser.name}`,
      tenantUserId: tenantUser.id,
    })
    const refundResponse = await Api(['getNewId', 'Invoice'], {
      order_id: orderResponse.data.id,
      isRefund: true,
      cashier_id: rootGetters['rummage/cashier']?.id,
      notAccountable: invoice.notAccountable,
      customer_id: invoice.customer_id,
    })
    commit('refunded', invoice)
    commit('refund', new Refund({
      id: orderResponse.data.id,
      opened,
      lockedBy,
      order,
      invoice,
      refund: refundResponse.data,
      tenantUser,
      voidReasons,
    }))
  },
  async signRefund ({ state, commit, dispatch, rootState, rootGetters }) {
    if (state.refund === null || ! rootGetters['fiscalCountry/shouldSignInvoice']) {
      return
    }
    const response = await Api(['save', 'Order'], {
      ...state.refund,
      saveToken: String.random(32),
      lockedBy: rootState.UUID,
    })
    commit('refund', new Order(response.data))
    dispatch('log', { action: 'Saved before signing', model: 'Order', model_id: state.refund.id }, { root: true })

    const invoice = state.refund.orderInvoices[0]
    await dispatch('fiscalCountry/signInvoice', invoice, { root: true })
    dispatch('log', { action: `Signed invoice ${invoice.number}`, model: 'Order', model_id: state.refund.id }, { root: true })
  },
  async closeRefund ({ state, commit, dispatch }) {
    if (state.refund === null || state.refunded === null) {
      return
    }
    const response = await Api(['save', 'Order'], {
      ...state.refund,
      saveToken: String.random(32),
      lockedBy: null,
    })
    commit('refund', new Order(response.data))
    dispatch('log', { action: `Refunded invoice ${state.refunded.number}`, model: 'Order', model_id: state.refund.id }, { root: true })
    try {
      await Api(['markAsRefunded', 'Invoice'], { invoice_id: state.refunded.id })
      dispatch('log', { action: 'Marked as refunded', model: 'Invoice', model_id: state.refunded.id }, { root: true })
      commit('markAsRefunded') // REV-23002
    } catch (e) {
      dispatch('log', { action: `Cannot mark as refunded ${e.response.status} ${e.response.data.message || e.response.statusText}`, model: 'Invoice', model_id: state.refunded.id }, { root: true })
    }
  },
  async cancelRefund ({ state, dispatch, rootGetters }) {
    if (state.refund === null) {
      return
    }
    try {
      await Api('setFree2Order', {
        id: state.refund.id,
        warehouse_id: rootGetters['rummage/cashier'].warehouse_id,
      })
      dispatch('log', { action: 'Set free (refund failed)', model: 'Order', model_id: state.refund.id }, { root: true })
    } catch (e) {
      dispatch('log', { action: `Cannot set free ${e.response.status} ${e.response.data.message || e.response.statusText}`, model: 'Order', model_id: state.refund.id }, { root: true })
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
